import React, { useContext, useEffect, useMemo, useState } from 'react';

import { BlueprintInstanceType, Components, Panel, PanelItem, PanelItemGroup, useSubscription } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { renderTracking } from '@r-services/render-tracking';
import { checkIsUtilityPanel as checkIsUtilityPanelHelper, getLabelColor, getLabelText as getLabelTextHelper } from '../helpers/sku-labels';

export type RenderSkuLayerProps = {
	panelId: string;
};

/**
 * Represents a collected SKU label with positioning info
 */
type SkuLabelData = {
	id: string;
	text: string;
	color: string;
	x: number;
	y: number;
	adjustedY?: number; // Y position after overlap adjustment
	panelItem: PanelItem;
	panelItemGroup: PanelItemGroup;
};

/**
 * Minimum vertical distance (in inches) between label centers to avoid overlap
 * Based on label height of approximately 2 inches (font size 1.5 + padding)
 */
const MIN_LABEL_VERTICAL_SPACING = 2.5;

/**
 * Maximum horizontal distance (in inches) to consider labels as potentially overlapping
 * Labels on different X positions don't need vertical spacing adjustment
 */
const MAX_HORIZONTAL_DISTANCE_FOR_OVERLAP = 8;

/**
 * Adjusts label Y positions to prevent overlap
 * Labels that are close together horizontally and vertically will be spread apart
 * @param labels Array of label data sorted by Y position
 * @returns Array with adjustedY values set to prevent overlap
 */
const adjustLabelsForOverlap = (labels: SkuLabelData[]): SkuLabelData[] => {
	if (labels.length <= 1) {
		return labels.map(l => ({ ...l, adjustedY: l.y }));
	}

	// Create a working copy with initial adjustedY
	const adjustedLabels = labels.map(l => ({ ...l, adjustedY: l.y }));

	// Group labels by similar X position (within horizontal distance threshold)
	// Then adjust Y positions within each group to prevent overlap
	for (let i = 1; i < adjustedLabels.length; i++) {
		const current = adjustedLabels[i];

		// Check against all previous labels for potential overlap
		for (let j = i - 1; j >= 0; j--) {
			const previous = adjustedLabels[j];

			// Only check overlap if labels are close horizontally
			const horizontalDistance = Math.abs(current.x - previous.x);
			if (horizontalDistance > MAX_HORIZONTAL_DISTANCE_FOR_OVERLAP) {
				continue;
			}

			// Check if labels would overlap vertically
			const verticalDistance = Math.abs(current.adjustedY! - previous.adjustedY!);
			if (verticalDistance < MIN_LABEL_VERTICAL_SPACING) {
				// Push current label down to create minimum spacing
				// Ensure we move in the same direction as the natural order (top to bottom)
				const adjustment = MIN_LABEL_VERTICAL_SPACING - verticalDistance;
				if (current.adjustedY! >= previous.adjustedY!) {
					current.adjustedY = previous.adjustedY! + MIN_LABEL_VERTICAL_SPACING;
				} else {
					current.adjustedY = previous.adjustedY! - MIN_LABEL_VERTICAL_SPACING;
				}
			}
		}
	}

	return adjustedLabels;
};

/**
 * RenderSkuLayer Component
 * Renders SKU labels for all items on a panel when the SKU layer is active.
 * Labels are:
 * - Color-coded by item type
 * - Sorted vertically by position (top to bottom)
 * - Rotated for readability on non-front panels
 * - Rendered at the topmost z-index
 */
export const RenderSkuLayer = ({ panelId }: RenderSkuLayerProps): React.ReactElement => {
	const { controller } = useContext(AppContext);
	const { kitchen, options } = controller;

	// Task 4: Get font size factor from options for scaling SKU labels
	// Note: Must use dimensionFontSizeFactor (the property with @Publish decorator) NOT dimensions.fontSizeFactor
	const fontSizeFactor = options?.dimensionFontSizeFactor ?? 1;

	// Subscribe to font size changes to re-render when fontSizeFactor is updated
	useSubscription(controller, false, [['designer-options-adjust-font-size', 'updated']]);

	// Track color changes to trigger re-renders when SKU colors are updated via context menu
	const [colorVersion, setColorVersion] = useState(0);

	// Subscribe to SKU color change events and panel item changes (add/delete)
	useEffect(() => {
		const subscriptions: string[] = [];

		// Subscribe to item_sku_color events from @Publish decorator on Item.skuColor setter
		subscriptions.push(kitchen.subscribe('item_sku_color', '*', () => {
			setColorVersion((v) => v + 1);
		}));

		// Subscribe to sku-label-color events from context menu
		subscriptions.push(controller.subscribe('sku-label-color', '*', () => {
			setColorVersion((v) => v + 1);
		}));

		// Subscribe to panel_item deletion events to refresh when appliances are deleted
		// This matches the pattern used by rules-engine.class.ts
		subscriptions.push(kitchen.subscribe('panel_item', 'deleted', () => {
			setColorVersion((v) => v + 1);
		}));

		// Subscribe to panel_items add events to refresh when new appliances are added
		subscriptions.push(kitchen.subscribe('panel_items', 'add', () => {
			setColorVersion((v) => v + 1);
		}));

		// Subscribe to panel_item moved events to refresh positions
		subscriptions.push(kitchen.subscribe('panel_item', 'moved', () => {
			setColorVersion((v) => v + 1);
		}));

		return () => {
			subscriptions.forEach((subId) => {
				kitchen.unsubscribe(subId);
				controller.unsubscribe(subId);
			});
		};
	}, [kitchen, controller]);

	renderTracking.incrementRender('RenderSkuLayer');

	// Get panel reference
	const referenceMap = useMemo(() => {
		return kitchen.getReferenceMap(panelId);
	}, [panelId, kitchen]);

	const { panel } = referenceMap;

	// Determine rotation based on panel orientation
	const labelRotation = useMemo(() => {
		if (!panel) return 0;

		// Front panel: no rotation needed
		if (panel.isFrontPanel) {
			return 0;
		}

		// Get the panel's origin angle for proper rotation
		const originAngle = panel.getOriginAngle();

		// For non-front panels, rotate text to be upright
		// The originAngle tells us how much the panel is rotated from front
		// We want to counter-rotate the text so it appears upright
		return -originAngle;
	}, [panel]);

	// Collect all SKU labels with their positions
	// Dependency on colorVersion ensures recalculation when SKU colors change
	const skuLabels = useMemo<SkuLabelData[]>(() => {
		if (!panel) return [];

		const isUtilityPanel = checkIsUtilityPanelHelper(panel);
		const labels: SkuLabelData[] = [];

		// Iterate through all panel item groups
		panel.panelItemGroups.children.forEach((panelItemGroup: PanelItemGroup) => {
			const groupCenterX = panelItemGroup.center.x;

			// Iterate through items in each group
			panelItemGroup.items.children.forEach((panelItem: PanelItem) => {
				// Skip items where showLabel is false (no label/sku text should be shown)
				if (!panelItem.item.showLabel) {
					return;
				}

				// Skip inner items wrapped by a sleeve/jacket -- only the
				// jacket's label should show, not the appliance underneath.
				if (panelItem.sleeve !== null) {
					return;
				}

				// Skip TOP_ONLY items as they're on the countertop, not the panel
				// But include drop-in items (TOP_ONLY with distanceBelowCountertop > 0)
				const isTopOnly = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
				const hasShowImage = panelItem.item.component?.showImage ?? false;
				if (isTopOnly && !panelItem.isDropIn && !hasShowImage) {
					return;
				}

				const labelText = getLabelTextHelper(panelItem, isUtilityPanel);
				// Pass the custom skuColor from the item if set via context menu
				const labelColor = getLabelColor(panelItem, panelItem.item.skuColor ?? undefined, isUtilityPanel);

				// Calculate the absolute position on the panel
				// Group X + item center Y (vertical position on panel)
				labels.push({
					id: panelItem.id,
					text: labelText,
					color: labelColor,
					x: groupCenterX,
					y: panelItem.center.y,
					panelItem,
					panelItemGroup
				});
			});
		});

		// Sort by vertical position (top to bottom), but render vent labels first
		// (behind other labels) so non-vent items like outlets are visible on top
		labels.sort((a, b) => {
			const aVent = a.panelItem.item.category?.isVent ? 0 : 1;
			const bVent = b.panelItem.item.category?.isVent ? 0 : 1;
			if (aVent !== bVent) return aVent - bVent;
			return a.y - b.y;
		});

		// Adjust labels to prevent overlap
		return adjustLabelsForOverlap(labels);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panel, colorVersion]);

	// If no panel or no labels, render nothing
	if (!panel || skuLabels.length === 0) {
		return <></>;
	}

	// Calculate the panel's vertical center offset for label positioning
	const panelHeight = panel.size.height;
	const panelCenterY = -panelHeight / 2;

	// Label styling constants
	// Task 4: Apply font size factor to SKU labels
	const LABEL_FONT_SIZE = DIMENSION_FONT_SIZE * fontSizeFactor;
	const LABEL_PADDING_X = 0.5 * fontSizeFactor;
	const LABEL_PADDING_Y = 0.25 * fontSizeFactor;
	const LABEL_BORDER_RADIUS = 0.25 * fontSizeFactor;

	return (
		<g
			type="sku-layer"
			data-panel-id={panelId}
			style={{ pointerEvents: 'none' }}
		>
			{skuLabels.map((labelData) => {
				// Calculate label position
				// X: use the group's center X position
				// Y: panel center offset + item's adjusted Y position (handles overlap)
				const labelX = labelData.x;
				const labelY = panelCenterY + (labelData.adjustedY ?? labelData.y);

				// Estimate text width for background rect (approximate)
				const estimatedTextWidth = labelData.text.length * LABEL_FONT_SIZE * 0.6;
				const rectWidth = estimatedTextWidth + (LABEL_PADDING_X * 2);
				const rectHeight = LABEL_FONT_SIZE + (LABEL_PADDING_Y * 2);

				// Get context menu props for this panel item (triggers appliance context menu)
				const contextMenuProps = addContextMenu({
					menuKey: BlueprintInstanceType.PanelItem,
					id: labelData.id,
					center: { x: labelX, y: labelY }
				});

				return (
					<g
						key={labelData.id}
						transform={`translate(${labelX}, ${labelY}) rotate(${labelRotation})`}
						style={{ pointerEvents: 'auto', cursor: 'context-menu' }}
						data-panel-item-id={labelData.id}
						className="sku-label-group"
						{...contextMenuProps}
					>
						{/* Background rectangle with color coding */}
						<rect
							x={-rectWidth / 2}
							y={-rectHeight / 2}
							width={rectWidth}
							height={rectHeight}
							rx={LABEL_BORDER_RADIUS}
							ry={LABEL_BORDER_RADIUS}
							fill={labelData.color}
							stroke="#333333"
							strokeWidth={0.05}
							opacity={0.95}
						/>
						{/* Label text */}
						<text
							x={0}
							y={0}
							textAnchor="middle"
							dominantBaseline="central"
							fontSize={LABEL_FONT_SIZE}
							fontFamily="Arial, sans-serif"
							fontWeight="500"
							fill="#333333"
							className="rta-designer-component-label rta-noselect"
						>
							{labelData.text}
						</text>
					</g>
				);
			})}
		</g>
	);
};
